import React, { Component } from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import validate from 'validate.js'
import _cloneDeep from 'lodash/cloneDeep'
import _forEach from 'lodash/forEach'
import _merge from 'lodash/merge'
import _pick from 'lodash/pick'
import _set from 'lodash/set'
import _uniq from 'lodash/uniq'
import { Link } from 'react-router-dom'
import { SITES_PATH } from 'config'

import { getSiteInfo, sitesActions } from 'modules/sites'
import InputCollection from '../inputs-collection'
import ValidatedInput from '../../common/validated-input'
import AddSiteHints from './add-site-hints'


const customValidators = {
  isSitemaps(values) {
    if (!Array.isArray(values) || !values.some(val => typeof val === 'string' && val.length)) {
      return 'No sitemaps provided.'
    }
  },
  isUrl(value) {
    if (!value) {
      return 'Domain is required.'
    }

    /**
     * Validates url string with optional scheme and port.
     *
     * e.g.
     * domain.com // true
     * domain.com.ua // true
     * domain.com:8080 // true
     * http://domain.com // true
     * domain.com.ua/en // false
     *
     * @const isValidUrl
     * @type {boolean}
     */
    const isValidUrl = /^(http:\/\/www\.|https:\/\/www\.|http:\/\/|https:\/\/)?[a-z0-9]+([\-\.]{1}[a-z0-9]+)*\.[a-z]{2,5}(:[0-9]{1,5})?(\/)?$/gi.test(value);
    if (!isValidUrl) {
      return 'Domain is not valid. Check hints.'
    }
  },
  isAuth(value) {
    if (typeof value !== 'object') {
      return 'Enter credentials.'
    }

    if (!value.user) {
      return 'Enter username.'
    }

    if (!value.pass) {
      return 'Enter password.'
    }
  },
};

_merge(validate.validators, customValidators);


const constraints = {
  url: {
    isUrl: true,
  },
  name: {
    presence: { message: 'Site name is required.' },
  },
  sitemaps: {
    isSitemaps: true,
  },
  auth: {
    isAuth: true,
  },
};

class AddSiteWizard extends Component {
  static propTypes = {
    createSite: PropTypes.func.isRequired,
  };

  state = {
    widgets: ['url'],
    values: { url: '' },
    isLoading: false,
    final: false,
    errors: {},
  };

  onInputChange = e => {
    const { name, value } = e.target;
    this.setState(({ values }) => {
      const newValues = _cloneDeep(values);
      _set(newValues, name, value);
      return { values: newValues };
    });
  };

  onInputCollectionChange = collectionValues => {
    this.setState(({ values }) => {
      return {
        ...this.state,
        values: {
          ...values,
          sitemaps: collectionValues,
        },
      }
    });
  }

  handleFormSubmit = e => {
    e.preventDefault();
    const hasErrors = this.validateForm();

    if (!hasErrors) {
      this.setState({ errors: {} });
      this.state.final ?
        this.onSubmit() :
        this.verifySite();
    }
  }

  /**
   *
   * @returns {boolean} hasErrors
   */
  validateForm = () => {
    const { values, widgets } = this.state
    const currentConstraints = _pick(constraints, widgets);
    const errors = validate(values, currentConstraints, { fullMessages: false });
    let hasErrors = false;

    // Transform errors message to set into state object the first error message from messages array.
    _forEach(errors, (value, key) => {
      hasErrors = true;
      if (Array.isArray(value) && value.length) {
        errors[key] = value[0];
      }
    });

    if (hasErrors) {
      this.setState({ errors });
    }

    return hasErrors;
  }

  onSubmit = () => {
    const site = { ...this.state.values };

    this.props.createSite(site);
  };

  verifySite = () => {
    this.setState({ isLoading: true }, () => {
      getSiteInfo(this.state.values.url, this.state.values.auth)
        .then(response => {
          this.setState({ isLoading: false, errors: {} }, () => {
            if (response.statusCode === 200) {
              if (response.body.status === 401) {
                const authWidgetShown = this.state.widgets.indexOf('auth') !== -1;

                if (authWidgetShown) {
                  this.setState(state => {
                    return {
                      ...state,
                      errors: {
                        ...state.errors,
                        auth: 'Credentials are wrong.',
                      },
                    }
                  })
                }
                else {
                  this.showAuthWidget();
                }
              }
              else {
                this.updateSiteInfo(response.body)
              }
            }
            else if (response.errorMessage) {
              let formError = response.errorMessage.substr(response.errorMessage.indexOf('Error: '));

              if (formError.indexOf('ENOTFOUND') !== -1) {
                formError = 'Domain is invalid.'
              }

              this.setState({
                errors: {
                  form: formError,
                },
              })
            }
          });
        },
        error => {
          console.error(error.code); // eslint-disable-line no-console
          if (error.message) {
            this.setState({
              isLoading: false,
              errors: { form: error.message },
            });
          }
        },
        );
    });
  };

  updateSiteInfo = ({ title: name, url, sitemaps }) => {
    if (name || Array.isArray(sitemaps)) {
      this.setState({
        widgets: _uniq(this.state.widgets.concat(['name', 'sitemaps'])),
        values: { ...this.state.values, name, url, sitemaps },
        final: true,
      })
    }
  }

  showAuthWidget = () => {
    this.setState(({ widgets }) => {
      return {
        widgets: _uniq(widgets.concat(['auth'])),
      };
    });
  }

  renderWidgets = () => {
    return this.state.widgets.map(widget => {
      const method = 'render' + widget[0].toUpperCase() + widget.slice(1);
      if (typeof this[method] === 'function') {
        return this[method](this.state.errors[widget]);
      }
    });
  };

  renderUrl = errorText => {
    const { url } = this.state.values;

    return (
      <ValidatedInput key="url" errorText={ errorText }>
        {
          ({ inputClassName, errorNode }) => {
            return (
              <div className="row">
                <label
                  className="col-sm-2 col-form-label"
                  htmlFor="url">Domain</label>
                <div className="col-sm-10">
                  <input
                    id="url"
                    className={ inputClassName }
                    name="url"
                    value={ url }
                    onChange={ this.onInputChange }
                    placeholder="example.com"
                    required
                  />
                  { errorNode }
                </div>
              </div>
            )
          }
        }
      </ValidatedInput>
    );
  };

  renderName = errorText => {
    const { name } = this.state.values;

    return (
      <ValidatedInput errorText={ errorText } key="name">
        { ({ inputClassName, errorNode }) => {
          return (
            <div className="row">
              <label
                className="col-sm-2 col-form-label text-nowrap"
                htmlFor="name">Site Name</label>
              <div className="col-sm-10">
                <input
                  id="name"
                  className={ inputClassName }
                  type="text"
                  name="name"
                  value={ name }
                  onChange={ this.onInputChange }
                  required
                />
                { errorNode }
              </div>
            </div>
          )
        } }
      </ValidatedInput>
    );
  };

  renderAuth = errorText => {
    const { auth } = this.state.values;

    return (
      <ValidatedInput errorText={ errorText }>
        { () => {
          return (
            <div className="form-group row">
              <label
                htmlFor="username"
                className="col-sm-2 col-form-label text-nowrap">Basic
                Auth</label>
              <div className="col-sm-5">
                <input
                  id="username"
                  className="form-control mb-sm-0 mb-3"
                  type="text"
                  name="auth.user"
                  onChange={ this.onInputChange }
                  placeholder="username"
                  defaultValue={ (auth || {}).user } autoComplete="off"
                />
              </div>
              <div className="col-sm-5">
                <input
                  id="password"
                  className="form-control"
                  type="password" name="auth.pass" autoComplete="new-password"
                  onChange={ this.onInputChange }
                  placeholder="password"
                  defaultValue={ (auth || {}).pass }
                />
              </div>
            </div>
          );
        } }
      </ValidatedInput>
    );
  };

  renderSitemaps = errorText => {
    const { sitemaps } = this.state.values;

    return (
      <ValidatedInput errorText={ errorText } key="sitemaps">
        {
          ({ errorNode }) => {
            return (
              <div className="row">
                <label className="col-sm-2 col-form-label">Sitemap
                  location(s)</label>
                <div className="col-sm-10">
                  <InputCollection
                    name="sitemaps[]"
                    defaultValue={ sitemaps }
                    onChange={ this.onInputCollectionChange }
                  />
                  { errorNode }
                </div>
              </div>
            )
          }
        }
      </ValidatedInput>
    );
  };

  render() {
    const { final, isLoading, errors } = this.state;

    return (
      <div>
        <div className="row">
          <h4 className="col-sm-12 mb-3">Add new site</h4>
        </div>
        <div className="row">
          <div className="col-lg-6">
            <form
              className="site-form mb-4"
              autoComplete="off"
              noValidate
              onSubmit={ this.handleFormSubmit }>
              { errors.form &&
              <div className="alert alert-danger">{ errors.form }</div> }
              { this.renderWidgets() }
              <div className="row text-nowrap">
                { isLoading ?
                  (<span className="offset-sm-2 col-sm-4">
                    <i className="zmdi zmdi-spinner zmdi-hc-spin mr-3" />Please wait...
                  </span>) :
                  (
                    <span className="col-sm-10 offset-sm-2">
                      <button className="btn btn-primary mr-3">
                        { final ? 'Save' : 'Next' }
                      </button>
                      <Link
                        to={ SITES_PATH }
                        className="btn btn-default">Cancel</Link>
                    </span>
                  )
                }
              </div>
            </form>
          </div>
          <div className="col-lg-6">
            <AddSiteHints />
          </div>
        </div>
      </div>
    );
  }
}

export default connect(null, { createSite: sitesActions.createSite })(AddSiteWizard)



// WEBPACK FOOTER //
// ./src/components/sites/wizard/index.js